import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import { getAllCategories, getStateAndCity } from '../../services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const validationSchema = yup.object().shape({
  name: yup.string().optional().max(50, 'Full name should be at most 50 characters'),
  business_name: yup.string().optional().max(100, 'Business name should be at most 100 characters'),
  phone_number: yup
    .string()
    .optional()
    .matches(/^[0-9]{10}$/, 'Invalid phone number'),
  email: yup.string().optional().email('Invalid email format'),
  category_id: yup.string().optional(),
  zip_code: yup
    .string()
    .optional()
    .matches(/^[0-9]{6}$/, 'Zip/Pin code should be exactly 6 digits'),
  country: yup.string().optional(),
  state: yup.string().optional(),
  city: yup.string().optional(),
  companyIds: yup.array().of(yup.number().integer().positive()).optional(),
  role_id: yup.number().integer().positive().optional(),
  userId: yup.number().optional()
});

export const EditVendor = ({ vendor, onClose, onSubmit }) => {
  const userId = Cookies.get('userId');
  const [categories, setCategories] = useState([]);
  const [city, setCity] = useState('Select');
  const [state, setState] = useState('-');
  const [cityDropdownOptions, setCityDropdownOptions] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: vendor?.name || '',
      business_name: vendor?.businessName || '',
      phone_number: vendor?.phone_number || '',
      email: vendor?.email || '',
      category_id: vendor?.category || '',
      zip_code: vendor?.pincode || '',
      country: vendor?.country || 'India',
      state: vendor?.state || '',
      city: vendor?.city || ''
    }
  });

  const { data: allCategories, refetch: refetchCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: getAllCategories
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Edit Vendor Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (allCategories?.data && allCategories?.data.length > 0) {
      setCategories(allCategories?.data);
    }
  }, [allCategories?.data]);

  // Handler for pincode changes
  const handlePincodeChange = useCallback(
    async (pincode) => {
      const cleanedPincode = pincode.replace(/[^0-9]/g, '');
      setValue('zip_code', cleanedPincode);

      if (cleanedPincode.length === 6) {
        try {
          const pincodeList = await getStateAndCity(cleanedPincode);
          const postOfficeList = pincodeList[0].PostOffice;

          if (postOfficeList.length > 0) {
            const newState = postOfficeList[0].State;
            const uniqueTehsils = [...new Set(postOfficeList.map((office) => office.Block))];

            setCityDropdownOptions(
              uniqueTehsils.map((tehsil) => ({ value: tehsil, label: tehsil }))
            );

            setValue('state', newState);
            setValue('city', uniqueTehsils[0]);

            setState(newState);
            setCity(uniqueTehsils[0]);
          } else {
            setValue('state', '-');
            setValue('city', 'Select');
            setCityDropdownOptions([]);
            setState('-');
            setCity('Select');
          }
        } catch (error) {
          toast.error('The entered pincode is invalid. Please enter a valid pincode.');
          setValue('state', '-');
          setValue('city', 'Select');
          setCityDropdownOptions([]);
          setState('-');
          setCity('Select');
        }
      }
    },
    [setValue]
  );

  // Load existing address data on mount
  useEffect(() => {
    if (vendor && vendor.pincode) {
      const pincodeStr = String(vendor.pincode);

      if (pincodeStr.length === 6) {
        getStateAndCity(pincodeStr)
          .then((pincodeList) => {
            const postOfficeList = pincodeList[0].PostOffice;
            if (postOfficeList.length > 0) {
              const uniqueTehsils = [...new Set(postOfficeList.map((office) => office.Block))];
              const cityOptions = uniqueTehsils.map((tehsil) => ({
                value: tehsil,
                label: tehsil
              }));
              setCityDropdownOptions(cityOptions);

              // Set state
              if (vendor.state) {
                setState(vendor.state);
                setValue('state', vendor.state);
              }

              // Set city - preserve existing value
              if (vendor.city) {
                setCity(vendor.city);
                setValue('city', vendor.city);
              }
            }
          })
          .catch((error) => {
            console.error('Error fetching pincode data:', error);
            // If fetch fails, still set existing values
            if (vendor.state) {
              setState(vendor.state);
            }
            if (vendor.city) {
              setCity(vendor.city);
            }
          });
      }
    }
  }, [vendor, setValue]);

  const onSubmitForm = (data) => {
    const formattedData = {
      name: data.name || undefined,
      businessName: data.business_name || undefined,
      email: data.email || undefined,
      phone_number: data.phone_number || undefined,
      category: data.category_id ? data.category_id.toUpperCase() : undefined,
      pincode: data.zip_code || undefined,
      country: data.country || undefined,
      state: data.state || undefined,
      city: data.city || undefined
    };

    onSubmit(formattedData);
    reset();
  };

  return (
    <>
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
          Edit Vendor Details
        </span>
      </div>
      <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col gap-4 pt-6">
        <div className="flex justify-between items-center w-full">
          <FormInput
            width={48}
            label={'Contact Person Name'}
            register_key={'name'}
            register={register}
            type={'text'}
            errors={errors.name}
          />
          <FormInput
            width={48}
            label={'Business Name'}
            register_key={'business_name'}
            register={register}
            type={'text'}
            errors={errors.business_name}
          />
        </div>

        <div className="flex justify-between items-center w-full">
          <FormInput
            width={48}
            label={'Phone Number'}
            register={register}
            register_key={'phone_number'}
            type={'text'}
            errors={errors.phone_number}
          />

          <FormInput
            width={48}
            label={'Email Address'}
            register={register}
            register_key={'email'}
            type={'text'}
            errors={errors.email}
          />
        </div>

        <div className="flex justify-between items-center w-full">
          <FormInput
            width={48}
            label={'Vendor Category'}
            register={register}
            register_key={'category_id'}
            type={'dropdown'}
            defaultValuePre={'Select Category'}
            options={[
              { value: '', label: 'Select Category' },
              ...categories.map((category) => ({
                value: category.id,
                label: category.name.trim()
              }))
            ]}
            errors={errors.category_id}
          />
        </div>

        {/* Address Details */}
        <div className="flex flex-col justify-between items-start gap-4">
          <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
            Address Details
          </span>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex justify-between space-x-4">
            <FormInput
              width={48}
              register={register}
              register_key={'zip_code'}
              label={'Zip/Pin Code'}
              type={'text'}
              maxLength={6}
              errors={errors.zip_code}
              onType={handlePincodeChange}
            />
            <FormInput
              width={48}
              register={register}
              register_key={'country'}
              label={'Select Country'}
              valuePre={'India'}
              errors={errors.country}
              CustomStyle={{ pointerEvents: 'none' }}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex justify-between space-x-4">
            <FormInput
              width={48}
              register={register}
              register_key={'state'}
              label={'State'}
              errors={errors.state}
              valuePre={state}
              CustomStyle={{ pointerEvents: 'none' }}
            />
            <FormInput
              width={48}
              register={register}
              register_key={'city'}
              label={'City'}
              valuePre={city}
              type={'dropdown'}
              options={cityDropdownOptions}
              errors={errors.city}
            />
          </div>
        </div>

        <div className="w-full flex justify-end items-center space-x-4 mt-8">
          <Button type="button" onClick={onClose} displayType="secondary">
            Cancel
          </Button>
          <Button type="submit">Update</Button>
        </div>
      </form>
    </>
  );
};

export default EditVendor;
